/**
 * NotebookBlockHandle — the Notion-style block handle: a drag grip that
 * reorders the block and a `+` that inserts a new empty paragraph below it.
 *
 * Both controls live in a portal on `document.body`, outside the editor's
 * overflow context (which is what was causing the React "insertBefore"
 * reconciliation errors).
 *
 * Two details make them actually usable:
 *
 *   - The handle must survive the pointer leaving the editor. The editor
 *     root listens for `mousemove`, and the handle is rendered outside that
 *     DOM, so the pointer's path from the text to the handle crosses the
 *     editor's boundary. Clearing the handle on `mouseleave` made it
 *     disappear before the click could land on it, so the clear is deferred
 *     and cancelled when the pointer reaches the handle.
 *   - The handle sits in the left margin of the block, not on top of the
 *     text, so it never steals a click meant for the first characters of a
 *     line.
 *
 * The grip drags: `dragstart` carries the block's ProseMirror range, the
 * editor root tracks the hovered block and shows a drop line, and `drop`
 * moves the block in one transaction.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { GripVertical, Plus } from 'lucide-react'
import { TextSelection } from '@tiptap/pm/state'
import type { Editor } from '@tiptap/react'

export interface NotebookBlockHandleProps {
  editor: Editor | null
}

interface HoverInfo {
  /** Position of the handle in viewport coords. */
  top: number
  left: number
  /** ProseMirror position in front of the block. */
  blockStart: number
  /** ProseMirror position behind the block. */
  blockEnd: number
}

interface DropHint {
  /** Viewport coords of the line that marks the drop position. */
  top: number
  left: number
  width: number
  /** ProseMirror position the block is moved to. */
  insertAt: number
}

/** How long the handle survives the pointer leaving the editor. */
const HIDE_DELAY = 600

export function NotebookBlockHandle({ editor }: NotebookBlockHandleProps) {
  const [hover, setHover] = useState<HoverInfo | null>(null)
  const [dropHint, setDropHint] = useState<DropHint | null>(null)
  const handleRef = useRef<HTMLDivElement | null>(null)
  const hideTimer = useRef<number | null>(null)
  const dropHintRef = useRef<DropHint | null>(null)
  const draggingRef = useRef(false)
  const dragRangeRef = useRef<{ from: number; to: number } | null>(null)

  useEffect(() => {
    const root = editor?.view?.dom as HTMLElement | undefined
    if (!root) return

    let raf = 0
    const onMove = (e: MouseEvent) => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const sel = editor!.state.selection
        if (!sel.empty) {
          setHover(null)
          return
        }
        const target = e.target as HTMLElement | null
        if (!target) return
        const block = target.closest('.ProseMirror > *') as HTMLElement | null
        if (!block) {
          setHover(null)
          return
        }
        const range = blockRange(editor!, block)
        if (!range) return
        const r = block.getBoundingClientRect()
        setHover({
          // Below the block, in the left margin.
          top: r.bottom + 2,
          left: Math.max(8, r.left - 30),
          blockStart: range.start,
          blockEnd: range.end,
        })
      })
    }

    const onLeave = () => {
      // The pointer is on its way to the handle, which lives outside the
      // editor. Only hide it if it does not arrive.
      if (hideTimer.current) window.clearTimeout(hideTimer.current)
      hideTimer.current = window.setTimeout(() => {
        hideTimer.current = null
        setHover(null)
      }, HIDE_DELAY)
    }

    root.addEventListener('mousemove', onMove)
    root.addEventListener('mouseleave', onLeave)
    return () => {
      root.removeEventListener('mousemove', onMove)
      root.removeEventListener('mouseleave', onLeave)
      if (raf) cancelAnimationFrame(raf)
      if (hideTimer.current) window.clearTimeout(hideTimer.current)
    }
  }, [editor])

  if (!editor) return null

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const ed = editor
    ed.chain()
      .focus()
      .insertContentAt(hover!.blockEnd, { type: 'paragraph' })
      // Move the cursor into the new paragraph.
      .setTextSelection(hover!.blockEnd + 1)
      .run()
  }

  /**
   * The grip drags with pointer events rather than HTML5 drag and drop:
   * the grip lives in a portal outside the editor, and pointer events work
   * the same for a mouse and for a touch.
   */
  const handlePointerDown = (e: React.PointerEvent) => {
    if (!hover) return
    e.preventDefault()
    draggingRef.current = true
    dragRangeRef.current = { from: hover.blockStart, to: hover.blockEnd }
    document.body.style.userSelect = 'none'
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    const hint = dropHintAt(editor!, e.clientX, e.clientY)
    const current = dropHintRef.current
    if (hint && current && current.insertAt === hint.insertAt && current.top === hint.top) return
    dropHintRef.current = hint
    setDropHint(hint)
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    document.body.style.userSelect = ''
    const range = dragRangeRef.current
    const hint = dropHintRef.current
    dragRangeRef.current = null
    dropHintRef.current = null
    setDropHint(null)
    if (!range || !hint) return
    moveBlock(editor!, range.from, range.to, hint.insertAt)
  }

  return createPortal(
    <>
      {dropHint && (
        <div
          className="pointer-events-none fixed z-[56] h-0.5 rounded-full bg-brand-500"
          style={{ top: dropHint.top - 1, left: dropHint.left, width: dropHint.width }}
          data-testid="block-drop-line"
        />
      )}
      {hover && (
        <div
          ref={handleRef}
          className="z-[55] flex select-none items-center gap-0.5 rounded-md bg-white px-1 py-0.5 text-surface-400 shadow-sm transition-opacity duration-100 hover:bg-surface-100 hover:text-surface-700 dark:bg-surface-800 dark:text-surface-500 dark:hover:bg-surface-700 dark:hover:text-surface-200"
          style={{
            position: 'fixed',
            top: hover.top,
            left: hover.left,
          }}
          contentEditable={false}
          onMouseEnter={() => {
            if (hideTimer.current) {
              window.clearTimeout(hideTimer.current)
              hideTimer.current = null
            }
          }}
          onMouseLeave={() => setHover(null)}
        >
          <button
            type="button"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onMouseDown={(e) => e.preventDefault()}
            title="Drag to reorder"
            aria-label="Drag to reorder"
            style={{ touchAction: 'none' }}
            className="cursor-grab rounded px-0.5 leading-none active:cursor-grabbing"
          >
            <GripVertical size={12} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={handleClick}
            onMouseDown={(e) => e.preventDefault()}
            title="Add block below"
            aria-label="Add block below"
            className="rounded px-0.5 leading-none active:scale-95"
          >
            <Plus size={12} aria-hidden="true" />
          </button>
        </div>
      )}
    </>,
    document.body,
  )
}

/**
 * The drop position under the pointer: the block that is hovered decides
 * whether the dragged block lands above or below it.
 */
function dropHintAt(editor: Editor, x: number, y: number): DropHint | null {
  const element = document.elementFromPoint(x, y)
  const block = element?.closest('.ProseMirror > *') as HTMLElement | null
  if (!block) return null
  const range = blockRange(editor, block)
  if (!range) return null
  const r = block.getBoundingClientRect()
  const before = y < r.top + r.height / 2
  return {
    top: before ? r.top : r.bottom,
    left: r.left,
    width: r.width,
    insertAt: before ? range.start : range.end,
  }
}

/** The ProseMirror range of a top-level block element. */
function blockRange(editor: Editor, block: HTMLElement): { start: number; end: number } | null {
  try {
    const inside = editor.view.posAtDOM(block, 0)
    const end = editor.view.posAtDOM(block, block.childNodes.length)
    return { start: inside - 1, end: end + 1 }
  } catch {
    return null
  }
}

/** Moves the block in `[from, to)` to `target` in one transaction. */
function moveBlock(editor: Editor, from: number, to: number, target: number): void {
  const { state, view } = editor
  // Dropping the block onto itself is a no-op.
  if (target >= from && target <= to) return
  const node = state.doc.nodeAt(from)
  if (!node) return
  let tr = state.tr.delete(from, to)
  const insertAt = target > from ? target - (to - from) : target
  tr = tr.insert(insertAt, node)
  tr.setSelection(TextSelection.create(tr.doc, insertAt + 1))
  view.dispatch(tr)
}
